import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CreateMedicalClearanceInput,
  ProviderType,
  PROVIDER_TYPE_LABELS,
  MedicalClearance,
} from "@/hooks/use-medical-clearances";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, Loader2, Stethoscope } from "lucide-react";
import { cn } from "@/lib/utils";

interface AddClearanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  onAdd: (input: CreateMedicalClearanceInput) => Promise<MedicalClearance | null>;
  adding: boolean;
}

export function AddClearanceDialog({
  open,
  onOpenChange,
  clientId,
  onAdd,
  adding,
}: AddClearanceDialogProps) {
  const [providerName, setProviderName] = useState("");
  const [providerType, setProviderType] = useState<ProviderType>("physician");
  const [clearanceDate, setClearanceDate] = useState<Date>(new Date());
  const [expiresAt, setExpiresAt] = useState<Date | undefined>();
  const [notes, setNotes] = useState("");

  const resetForm = () => {
    setProviderName("");
    setProviderType("physician");
    setClearanceDate(new Date());
    setExpiresAt(undefined);
    setNotes("");
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    if (!providerName.trim()) {
      toast.error("Please enter the provider name");
      return;
    }

    const input: CreateMedicalClearanceInput = {
      client_id: clientId,
      provider_name: providerName.trim(),
      provider_type: providerType,
      clearance_date: format(clearanceDate, "yyyy-MM-dd"),
      expires_at: expiresAt ? format(expiresAt, "yyyy-MM-dd") : null,
      notes: notes.trim() || undefined,
    };

    const result = await onAdd(input);
    if (result) {
      toast.success("Medical clearance added");
      handleClose();
    } else {
      toast.error("Failed to add clearance");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Stethoscope className="h-5 w-5" />
            Add Medical Clearance
          </DialogTitle>
          <DialogDescription>
            Record a new medical clearance for this client
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Provider Name */}
          <div className="space-y-2">
            <Label htmlFor="providerName">Provider Name *</Label>
            <Input
              id="providerName"
              value={providerName}
              onChange={(e) => setProviderName(e.target.value)}
              placeholder="Dr. Smith"
              disabled={adding}
            />
          </div>

          {/* Provider Type */}
          <div className="space-y-2">
            <Label>Provider Type</Label>
            <Select
              value={providerType}
              onValueChange={(v) => setProviderType(v as ProviderType)}
              disabled={adding}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PROVIDER_TYPE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Clearance Date */}
          <div className="space-y-2">
            <Label>Clearance Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  disabled={adding}
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !clearanceDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {clearanceDate ? format(clearanceDate, "PPP") : "Select date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={clearanceDate}
                  onSelect={(date) => date && setClearanceDate(date)}
                  disabled={(date) => date > new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Expiry Date (Optional) */}
          <div className="space-y-2">
            <Label>Expires On (Optional)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  disabled={adding}
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !expiresAt && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {expiresAt ? format(expiresAt, "PPP") : "No expiry date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={expiresAt}
                  onSelect={setExpiresAt}
                  disabled={(date) => date <= new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <p className="text-xs text-muted-foreground">
              Leave empty if the clearance doesn't expire
            </p>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes about this clearance..."
              rows={2}
              disabled={adding}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={adding}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={adding}>
            {adding ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Adding...
              </>
            ) : (
              "Add Clearance"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
